import { BASE_URL } from 'config';
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  FormHelperText,
  Input,
  Avatar,
  IconButton,
  Checkbox,
  FormControlLabel,
  Grid,
  Autocomplete,
  Link,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";
import { useTheme } from "@mui/material/styles";
const EVENT_STATUS_OPTIONS = [
  { value: "scheduled", label: "مجدولة" },
  { value: "ongoing", label: "جارية" },
  { value: "finished", label: "منتهية" },
];

const NewEvents = ({ onClose, onSuccess, initialData }) => {
  const [controller] = useMaterialUIController();
  const darkMode = controller.darkMode;
  const theme = useTheme();
  const [formData, setFormData] = useState({
    event_category_id: initialData?.event_category_id || null,
    name: initialData?.name || "",
    description: initialData?.description || "",
    start_datetime: initialData?.start_datetime || "",
    end_datetime: initialData?.end_datetime || "",
    market_id: initialData?.market_id || null,
    historical_place_id: initialData?.historical_place_id || null,
    location_description: initialData?.location_description || "",
    images: [],
    imagesPreview: [],
    status: initialData?.status || EVENT_STATUS_OPTIONS[0].value,
  });
  const [dropdowns, setDropdowns] = useState({
    event_categories: [],
    markets: [],
    historical_places: [],
  });
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const imagesInputRef = useRef();

  useEffect(() => {
    setLoadingDropdowns(true);
    const token = localStorage.getItem("user-token");
    axios
      .get(BASE_URL + "/events/create", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const event_categories = res.data?.data?.event_categories || [];
        const markets = res.data?.data?.markets || [];
        const historical_places = res.data?.data?.historical_places || [];
        setDropdowns({
          event_categories,
          markets,
          historical_places,
        });
        // إذا كان هناك بيانات أولية (تعديل)، اربط القيم الافتراضية مع كائنات القوائم
        if (initialData) {
          setFormData((prev) => ({
            ...prev,
            event_category_id:
              event_categories.find(
                (cat) =>
                  cat.id === (initialData.event_category?.id || initialData.event_category_id)
              ) || null,
            market_id:
              markets.find((m) => m.id === (initialData.market?.id || initialData.market_id)) ||
              null,
            historical_place_id:
              historical_places.find(
                (h) =>
                  h.id === (initialData.historical_place?.id || initialData.historical_place_id)
              ) || null,
            start_datetime: initialData.start_datetime
              ? initialData.start_datetime.slice(0, 16)
              : "",
            end_datetime: initialData.end_datetime ? initialData.end_datetime.slice(0, 16) : "",
            images: [],
            imagesPreview: initialData.images || [],
            name: initialData.name || "",
            description: initialData.description || "",
            location_description: initialData.location_description || "",
            status: initialData.status || EVENT_STATUS_OPTIONS[0].value,
            id: initialData.id,
          }));
        }
      })
      .catch(() => setDropdowns({ event_categories: [], markets: [], historical_places: [] }))
      .finally(() => setLoadingDropdowns(false));
  }, [initialData]);

  const validateForm = (data = formData) => {
    const newErrors = {};
    if (!data.event_category_id) newErrors.event_category_id = "تصنيف الفعالية مطلوب";
    if (!data.name.trim()) newErrors.name = "اسم الفعالية مطلوب";
    if (!data.description.trim()) newErrors.description = "وصف الفعالية مطلوب";
    if (!data.start_datetime) newErrors.start_datetime = "تاريخ ووقت البداية مطلوب";
    if (!data.end_datetime) newErrors.end_datetime = "تاريخ ووقت النهاية مطلوب";
    if (!data.location_description.trim()) newErrors.location_description = "وصف الموقع مطلوب";
    // التحقق من الصور: إذا لم توجد صور جديدة ولا صور قديمة، أظهر الخطأ
    if (!data.images.length && (!data.imagesPreview || !data.imagesPreview.length)) {
      newErrors.images = "يجب رفع صورة واحدة على الأقل";
    }
    if (!data.status) newErrors.status = "حالة الفعالية مطلوبة";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      validateForm(updated);
      return updated;
    });
    setFormSubmitted(false);
  };

  const handleDropdownChange = (name, newValue) => {
    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };
      validateForm(updated);
      return updated;
    });
    setFormSubmitted(false);
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) =>
      ["image/png", "image/jpeg", "image/jpg"].includes(file.type)
    );
    setFormData((prev) => {
      const updated = {
        ...prev,
        images: validFiles,
        imagesPreview: validFiles.map((file) => URL.createObjectURL(file)),
      };
      validateForm(updated);
      return updated;
    });
    setFormSubmitted(false);
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => {
      const newImages = [...prev.images];
      const newImagesPreview = [...prev.imagesPreview];
      newImages.splice(index, 1);
      newImagesPreview.splice(index, 1);
      const updated = { ...prev, images: newImages, imagesPreview: newImagesPreview };
      validateForm(updated);
      return updated;
    });
    setFormSubmitted(false);
  };

  // create new event
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    if (!validateForm()) return; // validate the form
    try {
      const formPayload = new FormData();
      formPayload.append("event_category_id", formData.event_category_id.id);
      formPayload.append("name", formData.name);
      formPayload.append("description", formData.description);
      formPayload.append("start_datetime", formData.start_datetime);
      formPayload.append("end_datetime", formData.end_datetime);
      if (formData.market_id) formPayload.append("market_id", formData.market_id.id);
      if (formData.historical_place_id)
        formPayload.append("historical_place_id", formData.historical_place_id.id);
      formPayload.append("location_description", formData.location_description);
      formData.images.forEach((img) => formPayload.append("images[]", img));
      formPayload.append("status", formData.status);
      const token = localStorage.getItem("user-token");
      Swal.fire({
        title: "جاري الحفظ...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
      let response;
      if (formData.id) {
        response = await axios.post(
          `${BASE_URL}/events/update/${formData.id}`,
          formPayload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          BASE_URL + "/events/store",
          formPayload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      Swal.close();
      if (response.data.status) {
        await Swal.fire({
          icon: "success",
          title: "تم الحفظ!",
          text: "تم حفظ البيانات بنجاح",
          confirmButtonText: "موافق",
          willOpen: () => {
            const container = document.querySelector(".swal2-container");
            if (container) container.style.zIndex = "99999";
          },
        });
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        throw new Error(response.data.message || "حدث خطأ غير متوقع");
      }
    } catch (error) {
      Swal.close();
      const errorMessage = error.response?.data?.message || error.message || "حدث خطأ أثناء الحفظ";
      Swal.fire({
        icon: "error",
        title: "فشل الحفظ!",
        text: errorMessage,
        confirmButtonText: "موافق",
        willOpen: () => {
          const container = document.querySelector(".swal2-container");
          if (container) container.style.zIndex = "99999";
        },
      });
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      event_category_id: null,
      name: "",
      description: "",
      start_datetime: "",
      end_datetime: "",
      market_id: null,
      historical_place_id: null,
      location_description: "",
      images: [],
      imagesPreview: [],
      status: EVENT_STATUS_OPTIONS[0].value,
    });
    setErrors({});
  };

  const getOptionLabelById = (options, id) => {
    if (!id) return "";
    const found = options.find((opt) => opt.id === id);
    return found ? found.name : "";
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: "100%",
        mx: "auto",
        bgcolor: darkMode ? theme.palette.background.default : "background.paper",
        borderRadius: 3,
        boxShadow: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: { xs: 2, sm: 4 },
        color: darkMode ? "white !important" : "inherit",
      }}
    >
      <Typography variant="h5" fontWeight="bold" mb={2} align="center">
        {formData.id ? "تعديل فعالية" : "إضافة فعالية جديدة"}
      </Typography>
      <Grid container spacing={2}>
        {/* تصنيف الفعالية */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={!!errors.event_category_id}>
            <Autocomplete
              options={dropdowns.event_categories}
              getOptionLabel={(option) => option.name || ""}
              value={formData.event_category_id}
              onChange={(e, newValue) => handleDropdownChange("event_category_id", newValue)}
              loading={loadingDropdowns}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <>
                      تصنيف الفعالية <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  error={!!errors.event_category_id}
                  helperText={errors.event_category_id}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </FormControl>
        </Grid>
        {/* اسم الفعالية */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={!!errors.name}>
            <TextField
              label={
                <>
                  اسم الفعالية <span style={{ color: "red" }}>*</span>
                </>
              }
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              inputProps={{ maxLength: 255 }}
            />
          </FormControl>
        </Grid>

        {/* وقت وتاريخ البداية */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={!!errors.start_datetime}>
            <TextField
              label="تاريخ ووقت البداية"
              name="start_datetime"
              type="datetime-local"
              value={formData.start_datetime}
              onChange={handleChange}
              error={!!errors.start_datetime}
              helperText={errors.start_datetime}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        {/* وقت وتاريخ النهاية */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth error={!!errors.end_datetime}>
            <TextField
              label="تاريخ ووقت النهاية"
              name="end_datetime"
              type="datetime-local"
              value={formData.end_datetime}
              onChange={handleChange}
              error={!!errors.end_datetime}
              helperText={errors.end_datetime}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        {/* وصف الفعالية */}
        <Grid item size={{ xs: 12 }}>
          <FormControl fullWidth error={!!errors.description}>
            <TextField
              label="وصف الفعالية"
              name="description"
              value={formData.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
              fullWidth
              multiline
              minRows={3}
            />
          </FormControl>
        </Grid>
        {/* السوق المرتبط */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <Autocomplete
              options={dropdowns.markets}
              getOptionLabel={(option) => option.name || ""}
              value={formData.market_id}
              onChange={(e, newValue) => handleDropdownChange("market_id", newValue)}
              loading={loadingDropdowns}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="السوق المرتبط (اختياري)"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </FormControl>
        </Grid>
        {/* الموقع التاريخي */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <Autocomplete
              options={dropdowns.historical_places}
              getOptionLabel={(option) => option.name || ""}
              value={formData.historical_place_id}
              onChange={(e, newValue) => handleDropdownChange("historical_place_id", newValue)}
              loading={loadingDropdowns}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="الموقع التاريخي (اختياري)"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDropdowns ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </FormControl>
        </Grid>
        {/* وصف الموقع */}
        <Grid item size={{ xs: 12 }}>
          <FormControl fullWidth error={!!errors.location_description}>
            <TextField
              label="وصف الموقع"
              name="location_description"
              value={formData.location_description}
              onChange={handleChange}
              error={!!errors.location_description}
              helperText={errors.location_description}
              fullWidth
              multiline
              minRows={2}
            />
          </FormControl>
        </Grid>
        {/* صور الفعالية */}
        <Grid item size={{ xs: 12 }}>
          <FormControl fullWidth error={!!errors.images}>
            <Box display="flex" flexDirection="column" alignItems="flex-start" width="100%">
              <label htmlFor="event-images-upload" style={{ width: "100%" }}>
                <Input
                  type="file"
                  inputProps={{ accept: "image/png, image/jpeg, image/jpg", multiple: true, color: "text.main" }}
                  onChange={handleImagesChange}
                  sx={{ display: "none", }}
                  color='text.main'

                  id="event-images-upload"
                  inputRef={imagesInputRef}
                />
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  size="small"
                  sx={{ width: "100%", color: "text.main", borderColor: "text.border", fontSize: 14, py: 1 }}
                >
                  صور الفعالية
                </Button>
              </label>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {formData.imagesPreview.map((src, idx) => (
                  <Box key={idx} position="relative" display="inline-block">
                    <Avatar
                      src={src}
                      sx={{ width: 70, height: 70, border: "2px solid #ddd" }}
                      variant="rounded"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(idx)}
                      sx={{ position: "absolute", top: 0, right: 0, bgcolor: "white" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              {errors.images && <FormHelperText error>{errors.images}</FormHelperText>}
              <Typography variant="caption" color="text.main">
                يمكنك رفع عدة صور (jpg, jpeg, png) بحجم لا يتجاوز 2MB لكل صورة.
              </Typography>
            </Box>
          </FormControl>
        </Grid>
        {/* الحالة */}
        <Grid item size={{ xs: 12, md: 6 }} mt={1}>
          <FormControl fullWidth error={!!errors.status}>
            <TextField
              select
              label="حالة الفعالية"
              name="status"
              value={formData.status}
              onChange={handleChange}
              error={!!errors.status}
              helperText={errors.status}
              SelectProps={{ native: true }}
            >
              {EVENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          </FormControl>
        </Grid>
      </Grid>
      <Box mt={4} mb={2} px={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.main">
          * الحقول المطلوبة
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCancel}
            sx={{ px: 4, py: 1, borderRadius: 2, borderColor: "primary.dark" }}
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ px: 4, py: 1, color: "white !important", bgcolor: "primary.dark", "&:hover": { bgcolor: "primary.main" }, borderRadius: 2 }}
            disabled={formSubmitted || Object.keys(errors).length > 0}
          >
            حفظ
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default NewEvents;
